import React, { ReactNode, useContext, useEffect, useState, useRef } from "react";
import { User } from "oidc-client";
import { useUserManager } from "./UserManagerContext";
import { signInUser } from "./signInUser";

export const OidcUserContext = React.createContext<{user: User | undefined}>({ user: undefined});

export function useOidcUser(): User | undefined {
  return useContext(OidcUserContext)?.user;
}

export function OidcUserProvider(props: {
  children?: ReactNode;
}) {
  const [user, setUser] = useState<User | undefined>(undefined);
  const userManager = useUserManager();
  const hasTriedSignIn = useRef(false);

  useEffect(() => {
    if(!userManager) return;

    const userLoadedCallback = (user: User) => {
      // console.log("UserLoaded event called: ", user);
      setUser(user);
    };

    const userUnloadedCallback = () => {
      // console.log("UserUnloaded event called");
      setUser(undefined);
    };

    // console.log("Adding user event listeners");
    userManager.events.addUserLoaded(userLoadedCallback);
    userManager.events.addUserUnloaded(userUnloadedCallback);

    // Cleanup function to remove event listeners
    return () => {
      // console.log("Removing user event listeners");
      userManager.events.removeUserLoaded(userLoadedCallback);
      userManager.events.removeUserUnloaded(userUnloadedCallback);
    };
  }, [userManager]);

  useEffect(() => {
    if (!userManager || hasTriedSignIn.current) {
      return;
    }

    hasTriedSignIn.current = true;
    // console.info("(UserProvider) trying to get user")

    signInUser(userManager)
      .then(user => {
        // console.info("(UserProvider) got user: ", user);
        setUser(user || undefined);
      })
      .catch(error => {
        console.error("(UserProvider) signInUser failed: ", error);
        setUser(undefined);
      });
  }, [userManager]);

  // Reset the ref when userManager changes
  useEffect(() => {
    hasTriedSignIn.current = false;
  }, [userManager]);

  // console.info("(UserProvider) Got user => rendering childs", user);

  return (
    <OidcUserContext.Provider value={{ user }}>
      {props.children}
    </OidcUserContext.Provider>
  );
}
